import { postClientAuditEvent } from "../api/auditLogApi";

const DEDUPE_MS = 1200;
const recentKeys = new Map<string, number>();

function shouldSkipElement(el: Element): boolean {
  if (el.closest("[data-audit-skip]")) return true;
  if (el.closest(".sidebar-flyout-backdrop")) return true;
  return false;
}

function labelFromElement(el: HTMLElement): string | null {
  const explicit = el.getAttribute("data-audit-label")?.trim();
  if (explicit) return explicit.slice(0, 200);

  const aria = el.getAttribute("aria-label")?.trim();
  if (aria) return aria.slice(0, 200);

  const title = el.getAttribute("title")?.trim();
  if (title) return title.slice(0, 200);

  const text = el.textContent?.replace(/\s+/g, " ").trim();
  if (text && text.length >= 2) return text.slice(0, 200);

  const name = el.getAttribute("name")?.trim();
  if (name) return name.slice(0, 200);

  const id = el.getAttribute("id")?.trim();
  if (id) return id.slice(0, 200);

  return null;
}

function dedupeKey(path: string, label: string, kind: string) {
  return `${kind}|${path}|${label}`;
}

function recentlyLogged(key: string) {
  const now = Date.now();
  const prev = recentKeys.get(key);
  if (prev != null && now - prev < DEDUPE_MS) return true;
  recentKeys.set(key, now);
  if (recentKeys.size > 200) {
    for (const [k, t] of recentKeys) {
      if (now - t > DEDUPE_MS * 4) recentKeys.delete(k);
    }
  }
  return false;
}

export type ClientAuditDetails = Record<string, unknown>;

/** Log a named UI action (button, workflow step, dialog confirm, etc.). */
export function logClientAction(label: string, details?: ClientAuditDetails) {
  const path = `${window.location.pathname}${window.location.search}`;
  const cleanLabel = label.trim().slice(0, 200);
  if (!cleanLabel) return;
  const key = dedupeKey(path, cleanLabel, "manual");
  if (recentlyLogged(key)) return;
  void postClientAuditEvent({
    eventType: "client_action",
    path,
    label: cleanLabel,
    details: { source: "manual", ...details },
  }).catch(() => {});
}

function logDomAction(
  kind: "click" | "submit",
  el: HTMLElement,
  extra?: ClientAuditDetails,
) {
  const path = `${window.location.pathname}${window.location.search}`;
  const baseLabel = labelFromElement(el);
  if (!baseLabel) return;

  const label =
    kind === "submit" ? `Submitted: ${baseLabel}` : `Clicked: ${baseLabel}`;
  const key = dedupeKey(path, label, kind);
  if (recentlyLogged(key)) return;

  void postClientAuditEvent({
    eventType: "client_action",
    path,
    label,
    details: {
      source: kind,
      tag: el.tagName.toLowerCase(),
      id: el.id || undefined,
      className: typeof el.className === "string" ? el.className.slice(0, 120) : undefined,
      ...extra,
    },
  }).catch(() => {});
}

/**
 * Captures button clicks and form submits across the signed-in console.
 * Skips link navigation (page views are logged separately).
 */
export function installClientActionAudit() {
  const onClick = (event: MouseEvent) => {
    if (event.button !== 0) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest("a[href]");
    if (anchor instanceof HTMLAnchorElement) {
      const href = anchor.getAttribute("href") ?? "";
      if (href.startsWith("/") || href.startsWith("#")) return;
    }

    const el = target.closest(
      "button, [role='button'], input[type='button'], input[type='submit'], .btn",
    );
    if (!(el instanceof HTMLElement)) return;
    if (shouldSkipElement(el)) return;
    if (el.closest("a[href]")) return;

    logDomAction("click", el);
  };

  const onSubmit = (event: SubmitEvent) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (shouldSkipElement(form)) return;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement) {
      logDomAction("submit", submitter, { formId: form.id || undefined, formName: form.name || undefined });
      return;
    }

    const label =
      form.getAttribute("data-audit-label")?.trim() ||
      form.getAttribute("name")?.trim() ||
      form.id?.trim() ||
      "Form";
    const path = `${window.location.pathname}${window.location.search}`;
    const fullLabel = `Submitted: ${label}`;
    const key = dedupeKey(path, fullLabel, "submit");
    if (recentlyLogged(key)) return;
    void postClientAuditEvent({
      eventType: "client_action",
      path,
      label: fullLabel.slice(0, 200),
      details: { source: "submit", formId: form.id || undefined, formName: form.name || undefined },
    }).catch(() => {});
  };

  document.addEventListener("click", onClick, true);
  document.addEventListener("submit", onSubmit, true);

  const onChange = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (shouldSkipElement(target)) return;

    if (target instanceof HTMLSelectElement) {
      const field =
        target.getAttribute("data-audit-label")?.trim() ||
        target.getAttribute("name")?.trim() ||
        target.id?.trim() ||
        "Dropdown";
      const value = target.selectedOptions[0]?.textContent?.replace(/\s+/g, " ").trim() || target.value;
      const path = `${window.location.pathname}${window.location.search}`;
      const label = `Changed ${field}: ${value}`.slice(0, 200);
      const key = dedupeKey(path, label, "change");
      if (recentlyLogged(key)) return;
      void postClientAuditEvent({
        eventType: "client_action",
        path,
        label,
        details: { source: "select", field, value: target.value },
      }).catch(() => {});
      return;
    }

    if (target instanceof HTMLInputElement && target.type === "file") {
      const file = target.files?.[0];
      if (!file) return;
      const field =
        target.getAttribute("data-audit-label")?.trim() ||
        target.getAttribute("name")?.trim() ||
        target.id?.trim() ||
        "File upload";
      const path = `${window.location.pathname}${window.location.search}`;
      const label = `Selected file for ${field}: ${file.name}`.slice(0, 200);
      const key = dedupeKey(path, label, "file");
      if (recentlyLogged(key)) return;
      void postClientAuditEvent({
        eventType: "client_action",
        path,
        label,
        details: { source: "file", field, fileName: file.name, fileSize: file.size },
      }).catch(() => {});
    }
  };

  document.addEventListener("change", onChange, true);

  return () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("submit", onSubmit, true);
    document.removeEventListener("change", onChange, true);
  };
}
